import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import './assets/main.css'
import '@hospora/ui/fonts.css'
import '@hospora/ui/style.css'
import { applyRememberedIdentity } from './lib/brand'
import { apiBaseUrl, storageKey } from './lib/runtime'
import * as Sentry from '@sentry/vue'
import { isIgnorableBrowserError, sentryEventMessage, watchOrgSuspension } from '@hospora-bookings/shared-ui'

// One place that notices "this venue's Hospora account is suspended" (402 org_suspended from any API call); App.vue shows the page.
watchOrgSuspension(apiBaseUrl())

// public/chunk-reload.js owns stale-chunk recovery. Allow a future recovery only after a
// route actually loads, never while the replacement assets are still failing.
router.afterEach((_to, _from, failure) => {
  if (failure) return
  try { sessionStorage.removeItem(storageKey('chunk-reload-attempted')) } catch { /* iframe storage denied */ }
})

// Embeds are white by default; hosts can opt into showing their own background.
// Store the mode on the document so it survives booking-step URL changes.
if (window.parent !== window) {
  document.documentElement.dataset.bookingBackground =
    new URLSearchParams(window.location.search).get('background') === 'transparent' ? 'transparent' : 'white'
}

// Start in the organisation's colours and typefaces from the last visit, until its configuration has loaded.
applyRememberedIdentity()

const app = createApp(App)

// Error reporting goes to the deployment's own Sentry project (VITE_SENTRY_DSN). JB & Sons' hostnames keep its
// project; any other organisation reports nowhere unless it sets its own: never into JB's.
const JB_SENTRY_DSN = 'https://10511191d6ae0df217ac43fdf0900d4c@o4511733872525313.ingest.de.sentry.io/4511758900789328'
const onJbHost = /(^|\.)jbandsons\.com\.au$/.test(window.location.hostname) || window.location.hostname === 'jb-venue-customer.pages.dev'
const sentryDsn = import.meta.env.VITE_SENTRY_DSN || (onJbHost ? JB_SENTRY_DSN : undefined)

Sentry.init({
  app,
  enabled: !!sentryDsn,
  dsn: sentryDsn,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', ...(import.meta.env.VITE_API_URL ? [import.meta.env.VITE_API_URL] : [])],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event, hint) {
    // Stale hashed chunks (recovered by chunk-reload.js) and scripts injected by browser
    // extensions / in-app browsers are not our bugs.
    if (isIgnorableBrowserError(sentryEventMessage(event, hint?.originalException))) return null
    return event
  },
})

app.use(createPinia())
app.use(router)
app.mount('#app')
